var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import classnames from 'classnames';
import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import tinycolor from 'tinycolor2';
import { getElementLeft, getElementTop } from '../_utils/dom';
import { createPrefixCls, textCase } from '../_utils/string';
import Icon from '../icon';
/** 当前动画状态 */
const ANIMATION_STATUS = {
    /** 初始状态 */
    READY: 'ready',
    /** 进场动画执行中 */
    ENTERING: 'entering',
    /** 进场动画已完毕 */
    ENTERED: 'entered',
    /** 离场动画执行中 */
    LEAVING: 'leaving',
    /** 离场动画已完毕 */
    LEAVED: 'leaved'
};
const ARROW_DISTANCE = 36 / 2;
const prefixCls = createPrefixCls('tooltip');
export const Tooltip = (_a) => {
    var { children, className, visible: propVisible, title, color = 'rgb(52, 106, 255)', placement = 'top', delay, closeable = true, onClose } = _a, restProps = __rest(_a, ["children", "className", "visible", "title", "color", "placement", "delay", "closeable", "onClose"]);
    const [visible, setVisible] = useState(propVisible);
    const [tooltipSty, setTooltipSty] = useState();
    const [animationStatus, setAnimationStatus] = useState(ANIMATION_STATUS.READY);
    const delayTimer = useRef();
    const $trigger = useRef();
    const $tooltip = useRef();
    const close = useCallback(() => {
        if (propVisible === undefined) {
            setAnimationStatus(ANIMATION_STATUS.LEAVING);
            window.removeEventListener('click', close);
        }
        else {
            onClose && onClose();
        }
    }, [onClose, propVisible]);
    useEffect(() => {
        if (delayTimer.current) {
            clearTimeout(delayTimer.current);
        }
        if (visible) {
            setAnimationStatus(ANIMATION_STATUS.ENTERING);
            window.addEventListener('click', close);
            if (delay) {
                delayTimer.current = window.setTimeout(() => {
                    setAnimationStatus(ANIMATION_STATUS.LEAVING);
                }, delay);
            }
        }
        else {
            setAnimationStatus(ANIMATION_STATUS.LEAVING);
        }
    }, [delay, close, propVisible, visible]);
    useEffect(() => {
        const style = {
            backgroundColor: color,
            WebkitBoxShadow: `0 5px 12px 0 ${tinycolor(color).setAlpha(0.3).toRgbString()}`
        };
        if (visible) {
            if ($trigger.current && $tooltip.current) {
                const { width, height } = $trigger.current.getBoundingClientRect();
                const top = getElementTop($trigger.current);
                const left = getElementLeft($trigger.current);
                const { width: toolTipWidth, height: toolTipheight } = $tooltip.current.getBoundingClientRect();
                if (placement.startsWith('top')) {
                    style.top = top - ARROW_DISTANCE - toolTipheight;
                    if (placement === 'top') {
                        style.left = left + (width - toolTipWidth) / 2;
                    }
                    else if (placement === 'topLeft') {
                        style.left = left;
                    }
                    else if (placement === 'topRight') {
                        style.left = left + width - toolTipWidth;
                    }
                }
                else if (placement.startsWith('right')) {
                    style.left = left + width + ARROW_DISTANCE;
                    if (placement === 'right') {
                        style.top = top + (height - toolTipheight) / 2;
                    }
                    else if (placement === 'rightTop') {
                        style.top = top;
                    }
                    else if (placement === 'rightBottom') {
                        style.top = top + height - toolTipheight;
                    }
                }
                else if (placement.startsWith('bottom')) {
                    style.top = top + height + ARROW_DISTANCE;
                    if (placement === 'bottom') {
                        style.left = left + (width - toolTipWidth) / 2;
                    }
                    else if (placement === 'bottomLeft') {
                        style.left = left;
                    }
                    else if (placement === 'bottomRight') {
                        style.left = left + width - toolTipWidth;
                    }
                }
                else if (placement.startsWith('left')) {
                    style.left = left - toolTipWidth - ARROW_DISTANCE;
                    if (placement === 'left') {
                        style.top = top + (height - toolTipheight) / 2;
                    }
                    else if (placement === 'leftTop') {
                        style.top = top;
                    }
                    else if (placement === 'leftBottom') {
                        style.top = top + height - toolTipheight;
                    }
                }
            }
        }
        setTooltipSty(style);
    }, [color, placement, visible]);
    const handleClick = useCallback(() => {
        var _a;
        const { onClick } = (_a = children) === null || _a === void 0 ? void 0 : _a.props;
        onClick && onClick();
        if (propVisible === undefined) {
            if (visible) {
                close();
            }
            else {
                setVisible(true);
            }
        }
    }, [children, close, propVisible, visible]);
    const handleAnimationEnd = useCallback(() => {
        if (animationStatus === ANIMATION_STATUS.LEAVING) {
            setVisible(false);
            onClose && onClose();
        }
    }, [animationStatus, onClose]);
    const handleClose = useCallback((e) => {
        e.stopPropagation();
        if (propVisible === undefined) {
            close();
        }
        else {
            onClose && onClose();
        }
    }, [close, onClose, propVisible]);
    const handleStopPropagation = useCallback((e) => {
        e.stopPropagation();
    }, []);
    const isVisible = useMemo(() => {
        return propVisible === undefined ? visible : propVisible;
    }, [propVisible, visible]);
    const tooltipCls = useMemo(() => {
        return classnames([
            prefixCls(),
            prefixCls(placement),
            animationStatus === ANIMATION_STATUS.ENTERING && prefixCls('animation-entering'),
            animationStatus === ANIMATION_STATUS.LEAVING && prefixCls('animation-leaving'),
            className
        ]);
    }, [animationStatus, className, placement]);
    const arrowCls = useMemo(() => {
        return classnames(prefixCls('arrow'), prefixCls(`arrow-${placement}`));
    }, [placement]);
    const arrowSty = useMemo(() => {
        let direction = 'top';
        if (placement.match(/^top/)) {
            direction = 'top';
        }
        else if (placement.match(/^right/)) {
            direction = 'right';
        }
        else if (placement.match(/^bottom/)) {
            direction = 'bottom';
        }
        else if (placement.match(/^left/)) {
            direction = 'left';
        }
        return {
            [`border${textCase(direction)}Color`]: color
        };
    }, [color, placement]);
    return (React.createElement(React.Fragment, null,
        React.isValidElement(children) &&
            React.cloneElement(children, {
                onClick: handleClick,
                ref: $trigger
            }),
        isVisible &&
            ReactDOM.createPortal(React.createElement("div", Object.assign({ ref: $tooltip, className: tooltipCls, style: tooltipSty, onClick: handleStopPropagation, onAnimationEnd: handleAnimationEnd }, restProps),
                React.createElement("div", { className: arrowCls, style: arrowSty }),
                React.createElement("div", { className: prefixCls('inner') },
                    React.createElement("div", { className: prefixCls('text') }, title),
                    closeable && React.createElement(Icon, { className: prefixCls('close'), name: "close", onClick: handleClose }))), document.body)));
};
export default Tooltip;
